// Morrow — Workflows (left-nav → Workflows).
//
// A Workflow is a reusable "what to do" the agent runs — one job each. It has three
// dials: WHAT it does (instruction), HOW it runs (trigger), and WHAT it may touch
// (apps + capabilities + autonomy). The app picker searches the FULL Composio
// catalog (~1000 apps) — not just what you've connected — so a workflow can be
// scoped to anything; connecting folds in when it first runs.
import {
  AlarmClockIcon,
  CopyIcon,
  DownloadIcon,
  FolderIcon,
  ListIcon,
  Loader2Icon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  UploadIcon,
  XIcon,
  ZapIcon as WorkflowIcon,
} from "lucide-react";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { ThreadId } from "@t3tools/contracts";

import { apiUrl } from "../../apiBase";
import { cached, invalidateCache } from "../../dataCache";
import { maybeOpenUpgradeFrom402 } from "../../upgradeModal";
import { resolveComposioUserId } from "../../composioConnect";
import {
  fetchVaultFolders,
  fetchVaultItems,
  type VaultFolder,
  type VaultItem,
} from "../../morrowVault";
import { type WorkflowRunLite } from "../../morrowAgents";
import { useI18n, type MessageKey } from "~/i18n";
import { threadEnvironment } from "~/state/threads";
import { useThreadRefs } from "~/state/entities";
import { useAtomCommand } from "~/state/use-atom-command";
import { Button } from "../ui/button";
import { InlineRename } from "./InlineRename";
import { RunStepsPanel } from "./RunStepsPanel";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { MorrowWorkspacePage } from "../workspace/MorrowWorkspacePage";
import {
  browserTimezone,
  buildCron,
  cronToHumanLocalized,
  dowLabels,
  nextLocalRuns,
  type Cadence,
  runDetailLabel,
} from "./scheduleI18n";
import { workflowMatchPhrase } from "./workflowMatchPhrases";
import {
  downloadWorkflowJson,
  parseWorkflowExportFile,
  removedEntries,
  type WorkflowExportDropped,
  type WorkflowExportFile,
  type WorkflowExportRejection,
  type WorkflowRemovedKey,
} from "./workflowTransfer";

type Workflow = {
  id: string;
  name: string;
  description: string;
  instruction: string;
  autonomous: boolean;
  enabled: boolean;
};

type WaTrigger = {
  id: string;
  name: string;
  matchKind: string;
  matchValue: string;
  enabled: boolean;
  workflowId: string | null;
};

/** A row from the unified morrow_workflow_triggers table
 * (comms/app-event/schedule/vault-item-added). */
type WfTrigger = {
  id: string;
  workflowId: string;
  kind: "comms" | "app-event" | "schedule" | "vault-item-added" | string;
  channel: string | null;
  toolkit: string | null;
  triggerSlug: string | null;
  matchKind: string;
  matchValue: string;
  scheduleCron: string | null;
  /** For `vault-item-added`: the vault folder being watched. */
  vaultFolderId?: string | null;
  enabled: boolean;
};

/** A Composio toolkit from the catalog search. */
type AppHit = { slug: string; name: string; logo: string };

/** Prettify a toolkit slug for display when we only have the slug (e.g. an
 * app-event trigger row): "googlecalendar" → "Googlecalendar". */
function prettifyApp(slug: string): string {
  return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug;
}

const PRIMARY_BUTTON =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60";
const GHOST_BUTTON =
  "inline-flex items-center justify-center gap-1.5 rounded-md border border-border/60 px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground disabled:opacity-60";
const INPUT =
  "w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40";

type Caps = { browser: boolean; memoryRead: boolean; memoryWrite: boolean; vault: boolean };
type Autonomy = "read" | "approval" | "autonomous";
type Channel = "whatsapp" | "telegram" | "slack" | "discord";
type WaMatch = "all" | "contact" | "keyword";

/** A trigger composed in the builder before the workflow exists — created on save.
 * Sources, plus from-chat (always on): COMMS (a message on a comms channel),
 * TRIGGERS (pick from the trigger assembly configured in Settings › Triggers),
 * SCHEDULE (a routine), and VAULT (a file landing in one of your vault folders). */
export type PendingTrigger =
  | { uid: string; kind: "whatsapp"; matchKind: WaMatch; matchValue: string }
  | { uid: string; kind: "comms"; channel: Channel; matchKind: WaMatch; matchValue: string }
  | {
      uid: string;
      kind: "app-event";
      toolkit: string;
      toolkitLabel: string;
      triggerSlug: string;
      triggerName: string;
    }
  | { uid: string; kind: "schedule"; cron: string; tz: string; summary: string }
  | { uid: string; kind: "vault-item-added"; folderId: string; folderName: string };

const CHANNEL_LABEL: Record<Channel, string> = {
  whatsapp: "WhatsApp Business",
  telegram: "Telegram",
  slack: "Slack",
  discord: "Discord",
};

async function postJson<T = unknown>(
  path: string,
  body: Record<string, unknown> = {},
): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    // A 402 plan gate pops the upgrade modal (dark until enforcement is on).
    if (await maybeOpenUpgradeFrom402(res)) return null;
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), { headers: { accept: "application/json" } });
    return res.ok ? ((await res.json()) as T) : null;
  } catch {
    return null;
  }
}

/**
 * `postJson` with the failure kept rather than collapsed to `null`.
 *
 * Import and duplicate are the two calls whose failure the user has to be told about —
 * import's 400 carries the only explanation of WHY a file was refused, and a silent
 * duplicate just looks broken. The 402 plan gate still goes through
 * `maybeOpenUpgradeFrom402` first (both calls hit `workflow.max`), and a `null` return
 * means exactly that: the upgrade modal is already on screen, so say nothing else.
 */
async function postJsonResult<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; data: (T & { error?: string }) | null } | null> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, data: null };
  }
  if (await maybeOpenUpgradeFrom402(res)) return null;
  let data: (T & { error?: string }) | null = null;
  try {
    data = (await res.json()) as T & { error?: string };
  } catch {
    data = null;
  }
  return { ok: res.ok, data };
}

/** What the import notice shows: either a refusal, or a success plus everything lost on the way. */
type ImportNotice =
  | { kind: "error"; reason: WorkflowExportRejection | "rejected"; detail?: string | undefined }
  | {
      kind: "ok";
      id: string | null;
      warnings: readonly string[];
      dropped: WorkflowExportDropped | undefined;
    };

const IMPORT_ERROR_KEY: Record<WorkflowExportRejection | "rejected", MessageKey> = {
  unreadable: "workflows.import.error.unreadable",
  "not-an-export": "workflows.import.error.notAnExport",
  "unsupported-version": "workflows.import.error.unsupportedVersion",
  rejected: "workflows.import.error.rejected",
};

/** Only the three genuine removals get a label — app-event and vault-folder triggers DO come
 *  across (minus their account-specific binding), and the server's `warnings` speak for them. */
const REMOVED_LABEL_KEY: Record<WorkflowRemovedKey, MessageKey> = {
  vaultFiles: "workflows.import.dropped.vaultFiles",
  contactTriggers: "workflows.import.dropped.contactTriggers",
  webhookUrls: "workflows.import.dropped.webhookUrls",
};

/** Persist one composed trigger against a saved workflow. Routes each kind to the
 * right backend: personal WhatsApp → its own table; other channels + app-events +
 * schedules → the unified workflow-triggers table (app-events also enable the
 * Composio subscription so events actually arrive). */
export async function createTriggerFor(workflowId: string, p: PendingTrigger): Promise<void> {
  if (p.kind === "whatsapp") {
    // Personal WhatsApp (Baileys) lives in its own table, not the unified
    // workflow-triggers one — same endpoint the AI composer uses.
    await postJson("/api/morrow/whatsapp-triggers", {
      name: "My WhatsApp",
      workflowId,
      matchKind: p.matchKind,
      matchValue: p.matchValue.trim(),
    });
  } else if (p.kind === "comms") {
    await postJson("/api/morrow/workflow-triggers", {
      workflowId,
      kind: "comms",
      channel: p.channel,
      matchKind: p.matchKind,
      matchValue: p.matchValue.trim(),
    });
  } else if (p.kind === "schedule") {
    await postJson("/api/morrow/workflow-triggers", {
      workflowId,
      kind: "schedule",
      scheduleCron: p.cron,
      tz: p.tz,
    });
  } else if (p.kind === "vault-item-added") {
    await postJson("/api/morrow/workflow-triggers", {
      workflowId,
      kind: "vault-item-added",
      vaultFolderId: p.folderId,
    });
  } else {
    // Route an existing Settings trigger (the assembly) to this workflow.
    await postJson("/api/morrow/workflow-triggers", {
      workflowId,
      kind: "app-event",
      toolkit: p.toolkit,
      triggerSlug: p.triggerSlug,
    });
  }
}

/** Human label for a composed trigger row. */
function pendingLabel(
  p: PendingTrigger,
  t: ReturnType<typeof useI18n>["t"],
  channelLabel: (channel: Channel) => string,
): string {
  if (p.kind === "whatsapp") {
    return `${t("workflows.setup.myWhatsapp")} · ${workflowMatchPhrase(p.matchKind, p.matchValue, t)}`;
  }
  if (p.kind === "comms") {
    return `${channelLabel(p.channel)} · ${workflowMatchPhrase(p.matchKind, p.matchValue, t)}`;
  }
  if (p.kind === "schedule") return p.summary;
  if (p.kind === "vault-item-added")
    return t("workflows.composer.vault.chip", { folder: p.folderName });
  return `${p.toolkitLabel} · ${p.triggerName}`;
}

/** App logo tile with monogram fallback (matches the Triggers/Connections look). */
function AppLogo({ hit }: { hit: AppHit }) {
  const [failed, setFailed] = useState(false);
  if (failed || !hit.logo) {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded bg-muted text-[8px] font-semibold uppercase text-muted-foreground">
        {hit.name.slice(0, 2)}
      </span>
    );
  }
  return (
    <img
      src={hit.logo}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      className="size-4 shrink-0 rounded object-contain"
    />
  );
}

/** Searchable picker over the WHOLE Composio catalog (~1000 apps). Selected apps
 * are pinned at the top; connected apps carry a dot. Empty selection = all apps. */
function AppScopePicker({
  selected,
  metaBySlug,
  connected,
  onToggle,
}: {
  selected: string[];
  metaBySlug: Record<string, AppHit>;
  connected: Set<string>;
  onToggle: (hit: AppHit) => void;
}) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<AppHit[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      const q = query.trim();
      const res = await getJson<{ toolkits?: AppHit[] }>(
        `/api/morrow/composio/toolkits?limit=24${q ? `&q=${encodeURIComponent(q)}` : ""}`,
      );
      if (mine === seq.current) {
        setHits(Array.isArray(res?.toolkits) ? res!.toolkits : []);
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [query]);

  // Results minus already-selected (selected are pinned separately, always visible).
  const results = useMemo(() => hits.filter((h) => !selected.includes(h.slug)), [hits, selected]);
  const selectedHits = useMemo(
    () => selected.map((s) => metaBySlug[s] ?? { slug: s, name: s, logo: "" }),
    [selected, metaBySlug],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60" />
        <input
          className="w-full rounded-md border border-border bg-transparent py-1.5 pl-8 pr-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-primary/40"
          placeholder={t("workflows.apps.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {selectedHits.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selectedHits.map((h) => (
            <button
              key={h.slug}
              type="button"
              onClick={() => onToggle(h)}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/50 bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
            >
              <AppLogo hit={h} />
              {h.name}
              <XIcon className="size-3 opacity-70" />
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex max-h-44 flex-wrap gap-1.5 overflow-y-auto">
        {loading && results.length === 0 ? (
          <span className="flex items-center gap-1.5 px-1 py-2 text-[12px] text-muted-foreground/70">
            <Loader2Icon className="size-3.5 animate-spin" /> {t("workflows.apps.searching")}
          </span>
        ) : results.length === 0 ? (
          <span className="px-1 py-2 text-[12px] text-muted-foreground/70">
            {t("workflows.apps.noMatch")}
          </span>
        ) : (
          results.map((h) => (
            <button
              key={h.slug}
              type="button"
              onClick={() => onToggle(h)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2 py-1 text-xs font-medium text-muted-foreground transition hover:text-foreground"
            >
              <AppLogo hit={h} />
              {h.name}
              {connected.has(h.slug) ? (
                <span
                  className="size-1.5 rounded-full bg-emerald-500"
                  title={t("workflows.apps.connected")}
                  aria-label={t("workflows.apps.connected")}
                />
              ) : null}
            </button>
          ))
        )}
      </div>
      <p className="text-[11px] text-muted-foreground/70">
        {selected.length === 0
          ? t("workflows.apps.scopeEmpty")
          : t("workflows.apps.scopeFenced", { count: selected.length })}
      </p>
    </div>
  );
}

/** An app-event trigger type offered by a connected app (from /triggers/available). */
type AvailTrigger = { slug: string; name: string };

// Local id for React list keys only (not persisted) — no crypto needed.
let uidSeq = 0;
const newUid = () => `t_${Date.now().toString(36)}_${(uidSeq += 1)}`;

/** Compose one trigger of any kind and emit it to the parent (which accumulates a
 * list). Used in the builder to add MANY triggers to a new workflow. */
/** One configured trigger from the Settings assembly (GET /api/morrow/triggers). */
type AssemblyTrigger = { toolkit: string; triggerSlug: string; status: string };

/**
 * `chat` is where a workflow with no triggers at all runs from — it is the real, already-active
 * source, not an absence of one. It gets a tab of its own so the picker can open on the truth:
 * the canvas says "From chat" and the picker now agrees, instead of opening on Comms message with
 * WhatsApp Business ticked and claiming a channel nobody chose.
 */
export type TriggerSource = "chat" | "whatsapp" | "comms" | "triggers" | "schedule" | "vault";

export function TriggerComposer({
  userId,
  onAdd,
  hasWhatsapp = false,
  openOn,
}: {
  userId: string;
  onAdd: (p: PendingTrigger) => void;
  /** Whether this workflow already has a personal-WhatsApp trigger — when true the
   * picker opens on "My WhatsApp" and marks it selected, so it's visible which
   * source is active (not just the chip above). */
  hasWhatsapp?: boolean;
  /**
   * Which source to open on — the kind this workflow ACTUALLY triggers on.
   *
   * Without it the picker always opened on "Comms message" with WhatsApp Business ticked, so a
   * schedule-only workflow looked like it was wired to WhatsApp and flatly contradicted the
   * schedule chip right above it.
   */
  openOn?: TriggerSource | undefined;
}) {
  const { t } = useI18n();
  const dayLabels = dowLabels(t);
  // Sources, plus from-chat (always on): My WhatsApp (personal, Baileys), a Comms
  // message (WhatsApp Business/Telegram/Slack/Discord), a configured Settings ›
  // Triggers app-event, or a Schedule (routine).
  const [source, setSource] = useState<TriggerSource>(
    openOn ?? (hasWhatsapp ? "whatsapp" : "chat"),
  );
  // Nothing is pre-ticked unless the workflow really does trigger on a comms channel — a ticked
  // box the author never chose reads as configuration that exists.
  const [channels, setChannels] = useState<Set<Channel>>(() =>
    openOn === "comms" ? new Set<Channel>(["whatsapp"]) : new Set<Channel>(),
  );
  // Schedule (Routine) picker state.
  const [cadence, setCadence] = useState<Cadence>("daily");
  const [schedTime, setSchedTime] = useState("09:00");
  const [schedDow, setSchedDow] = useState(1); // Monday
  const [schedMinute, setSchedMinute] = useState(0);
  // Interval N for the "every N minutes / hours" cadences.
  const [schedEvery, setSchedEvery] = useState(5);
  const [schedCustom, setSchedCustom] = useState("0 9 * * *");
  const [schedTz] = useState(() => browserTimezone());
  const toggleChannel = (c: Channel) =>
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  const [matchKind, setMatchKind] = useState<WaMatch>("all");
  const [matchValue, setMatchValue] = useState("");
  const [assembly, setAssembly] = useState<AssemblyTrigger[]>([]);
  // Vault-folder source: the folders you can watch, loaded when that tab is opened.
  const [vaultFolders, setVaultFolders] = useState<VaultFolder[] | null>(null);

  // Load the user's configured triggers when the Triggers source is opened.
  useEffect(() => {
    if (source !== "triggers" || !userId) return;
    let alive = true;
    void getJson<{ triggers?: AssemblyTrigger[] }>(
      `/api/morrow/triggers?userId=${encodeURIComponent(userId)}`,
    ).then((res) => {
      if (!alive) return;
      const rows = (Array.isArray(res?.triggers) ? res!.triggers : []).filter(
        (t) => t.status === "enabled",
      );
      setAssembly(rows);
    });
    return () => {
      alive = false;
    };
  }, [source, userId]);

  // Load the vault folders when the Vault source is opened. Fetched on open rather than on
  // mount so a workflow that never touches the vault costs nothing.
  useEffect(() => {
    if (source !== "vault" || vaultFolders !== null) return;
    let alive = true;
    void fetchVaultFolders().then((folders) => {
      if (alive) setVaultFolders(folders);
    });
    return () => {
      alive = false;
    };
  }, [source, vaultFolders]);

  // Group the assembly by app so it shows as a visual app list, not a dropdown.
  const assemblyApps = useMemo(() => {
    const byApp = new Map<string, AssemblyTrigger[]>();
    for (const t of assembly) {
      const list = byApp.get(t.toolkit) ?? [];
      list.push(t);
      byApp.set(t.toolkit, list);
    }
    return Array.from(byApp.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [assembly]);

  const canAdd = source === "comms" && channels.size > 0;

  const addComms = () => {
    for (const channel of channels) {
      onAdd({ uid: newUid(), kind: "comms", channel, matchKind, matchValue });
    }
    setMatchValue("");
    setMatchKind("all");
  };

  const addWhatsapp = () => {
    onAdd({ uid: newUid(), kind: "whatsapp", matchKind, matchValue });
    setMatchValue("");
    setMatchKind("all");
  };

  // The cron + human summary + preview derived from the friendly schedule picker.
  const schedCron = useMemo(
    () =>
      buildCron({
        cadence,
        time: schedTime,
        dow: schedDow,
        minute: schedMinute,
        every: schedEvery,
        custom: schedCustom,
      }),
    [cadence, schedTime, schedDow, schedMinute, schedEvery, schedCustom],
  );
  const schedSummary = useMemo(() => cronToHumanLocalized(schedCron, t), [schedCron, t]);
  const schedPreview = useMemo(() => nextLocalRuns(schedCron, 3, new Date()), [schedCron]);
  const addSchedule = () => {
    if (!schedCron.trim()) return;
    onAdd({ uid: newUid(), kind: "schedule", cron: schedCron, tz: schedTz, summary: schedSummary });
  };

  // Tapping a folder watches it: every file that lands there starts one run.
  const addVaultFolder = (folder: VaultFolder) => {
    onAdd({
      uid: newUid(),
      kind: "vault-item-added",
      folderId: folder.id,
      folderName: folder.name,
    });
  };

  // Tapping an app attaches all its enabled triggers (usually one) to the workflow.
  const addApp = (toolkit: string, rows: AssemblyTrigger[]) => {
    for (const r of rows) {
      onAdd({
        uid: newUid(),
        kind: "app-event",
        toolkit,
        toolkitLabel: prettifyApp(toolkit),
        triggerSlug: r.triggerSlug,
        triggerName: r.triggerSlug,
      });
    }
  };

  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-dashed border-border/60 p-3">
      <div className="flex flex-wrap gap-1.5">
        {(
          [
            ["chat", t("workflows.composer.source.chat")],
            ["whatsapp", `${hasWhatsapp ? "✓ " : ""}${t("workflows.setup.myWhatsapp")}`],
            ["comms", t("workflows.composer.source.comms")],
            ["triggers", t("workflows.composer.source.triggers")],
            ["schedule", t("workflows.composer.source.schedule")],
            ["vault", t("workflows.composer.source.vault")],
          ] as ReadonlyArray<[typeof source, string]>
        ).map(([val, label]) => (
          <button
            key={val}
            type="button"
            onClick={() => setSource(val)}
            className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
              source === val
                ? "border-primary/50 bg-primary/10 text-primary"
                : val === "whatsapp" && hasWhatsapp
                  ? "border-primary/30 bg-primary/5 text-foreground"
                  : "border-border/60 text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {source === "chat" ? (
        // Nothing to add: chat is already live. Say so, rather than leaving the author hunting for
        // an "add" button that would have no meaning here.
        <p className="text-[11px] text-muted-foreground/70">{t("workflows.composer.chat.hint")}</p>
      ) : source === "whatsapp" ? (
        <>
          <p className="text-[11px] text-muted-foreground/70">
            {hasWhatsapp
              ? t("workflows.composer.whatsapp.selected")
              : t("workflows.composer.whatsapp.hint")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${INPUT} w-auto`}
              value={matchKind}
              onChange={(e) => setMatchKind(e.target.value as WaMatch)}
            >
              <option value="all">{t("settings.triggers.whatsapp.match.all")}</option>
              <option value="contact">{t("settings.triggers.whatsapp.match.contact")}</option>
              <option value="keyword">{t("settings.triggers.whatsapp.match.keyword")}</option>
            </select>
            {matchKind !== "all" ? (
              <input
                className={`${INPUT} flex-1`}
                placeholder={
                  matchKind === "contact"
                    ? t("workflows.composer.matchPlaceholder.contact")
                    : t("workflows.composer.matchPlaceholder.keyword")
                }
                value={matchValue}
                onChange={(e) => setMatchValue(e.target.value)}
              />
            ) : null}
            <button type="button" className={GHOST_BUTTON} onClick={addWhatsapp}>
              {t("workflows.composer.addTrigger")}
            </button>
          </div>
        </>
      ) : source === "comms" ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {(["whatsapp", "telegram", "slack", "discord"] as Channel[]).map((c) => {
              const on = channels.has(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleChannel(c)}
                  className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                    on
                      ? "border-primary/50 bg-primary/10 text-primary"
                      : "border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {on ? "✓ " : ""}
                  {c === "whatsapp"
                    ? t("settings.comms.whatsapp.label")
                    : c === "telegram"
                      ? t("settings.comms.telegram.label")
                      : c === "slack"
                        ? t("settings.comms.slack.label")
                        : t("settings.comms.discord.label")}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className={`${INPUT} w-auto`}
              value={matchKind}
              onChange={(e) => setMatchKind(e.target.value as WaMatch)}
            >
              <option value="all">{t("settings.triggers.whatsapp.match.all")}</option>
              <option value="contact">{t("settings.triggers.whatsapp.match.contact")}</option>
              <option value="keyword">{t("settings.triggers.whatsapp.match.keyword")}</option>
            </select>
            {matchKind !== "all" ? (
              <input
                className={`${INPUT} flex-1`}
                placeholder={
                  matchKind === "contact"
                    ? t("workflows.composer.matchPlaceholder.contact")
                    : t("workflows.composer.matchPlaceholder.keyword")
                }
                value={matchValue}
                onChange={(e) => setMatchValue(e.target.value)}
              />
            ) : null}
            <button type="button" className={GHOST_BUTTON} onClick={addComms} disabled={!canAdd}>
              {t("workflows.composer.addTrigger")}
            </button>
          </div>
          {channels.has("discord") ? (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              {t("workflows.composer.discordNote")}
            </p>
          ) : null}
        </>
      ) : source === "schedule" ? (
        <div className="flex flex-col gap-2.5">
          {/* Friendly cadence picker → cron. Shows the plain-English summary + when it
              will next run, so you can see how the routine flows before adding it. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {(
              [
                ["daily", t("workflows.composer.cadence.daily")],
                ["weekdays", t("workflows.composer.cadence.weekdays")],
                ["weekly", t("workflows.composer.cadence.weekly")],
                ["hourly", t("workflows.composer.cadence.hourly")],
                ["everyNMinutes", t("workflows.composer.cadence.everyNMinutes")],
                ["everyNHours", t("workflows.composer.cadence.everyNHours")],
                ["custom", t("workflows.composer.cadence.custom")],
              ] as ReadonlyArray<[Cadence, string]>
            ).map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setCadence(val)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
                  cadence === val
                    ? "border-primary/50 bg-primary/10 text-primary"
                    : "border-border/60 text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {cadence === "weekly" ? (
              <select
                className={`${INPUT} w-auto`}
                value={schedDow}
                onChange={(e) => setSchedDow(Number(e.target.value))}
              >
                {dayLabels.map((d, i) => (
                  <option key={d} value={i}>
                    {d}
                  </option>
                ))}
              </select>
            ) : null}
            {cadence === "hourly" ? (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {t("workflows.composer.atMinute")}
                <input
                  type="number"
                  min={0}
                  max={59}
                  className={`${INPUT} w-16`}
                  value={schedMinute}
                  onChange={(e) =>
                    setSchedMinute(Math.min(59, Math.max(0, Number(e.target.value) || 0)))
                  }
                />
              </label>
            ) : cadence === "everyNMinutes" ? (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {t("workflows.composer.every.prefix")}
                <input
                  type="number"
                  min={1}
                  max={59}
                  className={`${INPUT} w-16`}
                  value={schedEvery}
                  onChange={(e) =>
                    setSchedEvery(Math.min(59, Math.max(1, Number(e.target.value) || 1)))
                  }
                />
                {t("workflows.composer.every.minutes")}
              </label>
            ) : cadence === "everyNHours" ? (
              <>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {t("workflows.composer.every.prefix")}
                  <input
                    type="number"
                    min={1}
                    max={23}
                    className={`${INPUT} w-16`}
                    value={schedEvery}
                    onChange={(e) =>
                      setSchedEvery(Math.min(23, Math.max(1, Number(e.target.value) || 1)))
                    }
                  />
                  {t("workflows.composer.every.hours")}
                </label>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {t("workflows.composer.atMinute")}
                  <input
                    type="number"
                    min={0}
                    max={59}
                    className={`${INPUT} w-16`}
                    value={schedMinute}
                    onChange={(e) =>
                      setSchedMinute(Math.min(59, Math.max(0, Number(e.target.value) || 0)))
                    }
                  />
                </label>
              </>
            ) : cadence === "custom" ? (
              <input
                className={`${INPUT} flex-1 font-mono`}
                placeholder={t("workflows.composer.cronPlaceholder")}
                value={schedCustom}
                onChange={(e) => setSchedCustom(e.target.value)}
              />
            ) : (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {t("workflows.composer.atTime")}
                <input
                  type="time"
                  className={`${INPUT} w-auto`}
                  value={schedTime}
                  onChange={(e) => setSchedTime(e.target.value)}
                />
              </label>
            )}
            <button
              type="button"
              className={GHOST_BUTTON}
              onClick={addSchedule}
              disabled={!schedCron.trim()}
            >
              {t("workflows.composer.addSchedule")}
            </button>
          </div>

          {/* How it will run — plain English + the next few fire times. */}
          <div className="rounded-md border border-border/50 bg-muted/20 px-2.5 py-2">
            <div className="flex items-center gap-1.5 text-[12px] font-medium text-foreground">
              <AlarmClockIcon className="size-3.5 shrink-0" aria-hidden />
              <span>{schedSummary}</span>
              <span className="text-[11px] font-normal text-muted-foreground/70">· {schedTz}</span>
            </div>
            {schedPreview.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {schedPreview.map((r, i) => (
                  <span
                    key={i}
                    className="rounded bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground ring-1 ring-border/50"
                  >
                    {i === 0 ? t("workflows.composer.schedule.next") : ""}
                    {r}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-1 text-[10px] text-muted-foreground/60">
                {t("workflows.composer.schedule.invalidCron")}
              </div>
            )}
          </div>
        </div>
      ) : source === "vault" ? (
        <div className="flex flex-col gap-2">
          <p className="text-[11px] text-muted-foreground/70">
            {t("workflows.composer.vault.hint")}
          </p>
          {vaultFolders === null ? (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground/70">
              <Loader2Icon className="size-3.5 animate-spin" aria-hidden />
              {t("workflows.composer.vault.loading")}
            </span>
          ) : vaultFolders.length === 0 ? (
            <p className="text-[12px] text-muted-foreground/70">
              {t("workflows.composer.vault.noFolders")}{" "}
              <Link to="/vault" className="underline hover:text-foreground">
                {t("workflows.composer.vault.goToVault")}
              </Link>
              .
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {vaultFolders.map((folder) => (
                <button
                  key={folder.id}
                  type="button"
                  onClick={() => addVaultFolder(folder)}
                  className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-xs font-medium text-foreground transition hover:border-primary/50 hover:bg-primary/5"
                >
                  <FolderIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  {folder.name}
                  <span className="text-[10px] text-muted-foreground">{folder.itemCount}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {assemblyApps.length === 0 ? (
            <p className="text-[12px] text-muted-foreground/70">
              {t("workflows.composer.noTriggersYet")}{" "}
              <Link to="/settings/triggers" className="underline hover:text-foreground">
                {t("workflows.composer.settingsTriggers")}
              </Link>
              {t("workflows.composer.thenPick")}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {assemblyApps.map(([toolkit, rows]) => (
                  <button
                    key={toolkit}
                    type="button"
                    onClick={() => addApp(toolkit, rows)}
                    title={rows.map((r) => r.triggerSlug).join(", ")}
                    className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-xs font-medium text-foreground transition hover:border-primary/50 hover:bg-primary/5"
                  >
                    <AppLogo
                      hit={{
                        slug: toolkit,
                        name: prettifyApp(toolkit),
                        logo: `https://logos.composio.dev/api/${toolkit}`,
                      }}
                    />
                    {prettifyApp(toolkit)}
                    {rows.length > 1 ? (
                      <span className="text-[10px] text-muted-foreground">×{rows.length}</span>
                    ) : null}
                  </button>
                ))}
              </div>
              <span className="text-[11px] text-muted-foreground/70">
                {t("workflows.composer.tapApp")}{" "}
                <Link to="/settings/triggers" className="underline hover:text-foreground">
                  {t("workflows.composer.settingsTriggers")}
                </Link>
                .
              </span>
            </>
          )}
        </>
      )}
    </div>
  );
}

export function WorkflowsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const channelLabel = useCallback(
    (channel: Channel) => {
      switch (channel) {
        case "whatsapp":
          return t("settings.comms.whatsapp.label");
        case "telegram":
          return t("settings.comms.telegram.label");
        case "slack":
          return t("settings.comms.slack.label");
        case "discord":
          return t("settings.comms.discord.label");
      }
    },
    [t],
  );
  const { isLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  const userId = useMemo(
    () => resolveComposioUserId(isLoaded && isSignedIn ? clerkUserId : null),
    [isLoaded, isSignedIn, clerkUserId],
  );

  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  /**
   * Did the last load FAIL, as opposed to finding nothing?
   *
   * The two were indistinguishable: `refresh` did `setWorkflows(wf?.workflows ?? [])`, and
   * `getJson` returns null for every failure — so a 401 while the Clerk session was still
   * bootstrapping emptied the list and the page said "No workflows yet". Creating one then
   * looked like it had done nothing, because the list never came back. An empty list is a
   * claim about the account; only a successful load may make it.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  // False until the first list fetch resolves, so the page shows skeletons instead of flashing
  // "No workflows yet" (the empty state) while the real list is still in flight.
  const [loaded, setLoaded] = useState(false);
  const [triggers, setTriggers] = useState<WaTrigger[]>([]);
  const [wfTriggers, setWfTriggers] = useState<WfTrigger[]>([]);
  const [connected, setConnected] = useState<Set<string>>(new Set());

  const [name, setName] = useState("");
  const [instruction, setInstruction] = useState("");
  const [allowedApps, setAllowedApps] = useState<string[]>([]);
  const [metaBySlug, setMetaBySlug] = useState<Record<string, AppHit>>({});
  const [caps, setCaps] = useState<Caps>({
    browser: false,
    memoryRead: true,
    memoryWrite: false,
    vault: false,
  });
  const [autonomy, setAutonomy] = useState<Autonomy>("approval");
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Default create = just name + description (the AI reasons out triggers/apps in the
  // plan phase). "Manual" reveals the trigger/app/capability/autonomy setup.
  const [manual, setManual] = useState(false);
  const [runsByWf, setRunsByWf] = useState<Record<string, WorkflowRunLite[]>>({});
  // Duplicate/export failures are one-liners above the list; an import always gets the
  // notice dialog, because it has warnings to read rather than a result to glance at.
  const [actionError, setActionError] = useState<string | null>(null);
  const [importNotice, setImportNotice] = useState<ImportNotice | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  // Triggers composed for THIS new workflow (any mix of kinds) — created on save.
  const [pending, setPending] = useState<PendingTrigger[]>([]);
  // Granular vault: the specific items this workflow may touch (empty + vault on = all).
  const [vaultAll, setVaultItemsList] = useState<VaultItem[]>([]);
  const [vaultSel, setVaultSel] = useState<string[]>([]);

  const toggleApp = useCallback((hit: AppHit) => {
    setMetaBySlug((m) => (m[hit.slug] ? m : { ...m, [hit.slug]: hit }));
    setAllowedApps((prev) =>
      prev.includes(hit.slug) ? prev.filter((a) => a !== hit.slug) : [...prev, hit.slug],
    );
  }, []);
  const toggleCap = useCallback((key: keyof Caps) => {
    setCaps((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const addPending = useCallback((p: PendingTrigger) => setPending((prev) => [...prev, p]), []);
  const removePending = useCallback(
    (uid: string) => setPending((prev) => prev.filter((x) => x.uid !== uid)),
    [],
  );

  const refresh = useCallback(async () => {
    // Read through the short-lived cache: moving between Workflows, Routines and Agents (or any
    // remount) re-ran all four of these, and each is a real query. Every write below invalidates
    // what it changed, so this never shows a stale list.
    const [wf, tr, wtr, rn] = await Promise.all([
      // A list renders a name and a short preview: ?brief=1 leaves the graph and the rest of each
      // instruction in Postgres instead of selecting them. The setup page, which needs both,
      // calls the same endpoint WITHOUT the flag.
      cached("/api/morrow/workflows?brief=1", () =>
        getJson<{ workflows?: Workflow[] }>("/api/morrow/workflows?brief=1"),
      ),
      cached("/api/morrow/whatsapp-triggers", () =>
        getJson<{ triggers?: WaTrigger[] }>("/api/morrow/whatsapp-triggers"),
      ),
      cached("/api/morrow/workflow-triggers", () =>
        getJson<{ triggers?: WfTrigger[] }>("/api/morrow/workflow-triggers"),
      ),
      // NOT cached: runs are the live thing a person watches. An 8s TTL here showed the state
      // from before a run started, which reads as "it did not start" — the opposite of the
      // reassurance the list is there to give. The three reads above are configuration and
      // change only when someone edits them.
      getJson<{ runs?: WorkflowRunLite[] }>("/api/morrow/workflows/runs"),
    ]);
    if (!wf) {
      // Keep whatever is on screen and say so. Clearing here is what turned a transient
      // auth/network failure into "you have no workflows".
      setLoadFailed(true);
      setLoaded(true);
      return;
    }
    setLoadFailed(false);
    setLoaded(true);
    setWorkflows(wf.workflows ?? []);
    setTriggers(tr?.triggers ?? []);
    setWfTriggers(wtr?.triggers ?? []);
    const byWf: Record<string, WorkflowRunLite[]> = {};
    for (const r of rn?.runs ?? []) (byWf[r.workflowId] ??= []).push(r);
    for (const list of Object.values(byWf))
      list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    setRunsByWf(byWf);
  }, []);

  /**
   * Re-read AFTER A WRITE: drop the cached lists first.
   *
   * The mount effect may serve from cache — that is the saving. A refresh that follows a
   * mutation may not, or the page shows the state from before the write (a renamed workflow
   * under its old name, a deleted one still listed). One prefix covers /workflows,
   * /workflows/runs and /workflow-triggers.
   */
  const refreshNow = useCallback(async () => {
    invalidateCache("/api/morrow/workflow");
    invalidateCache("/api/morrow/whatsapp-triggers");
    await refresh();
  }, [refresh]);

  // Stop a running run by interrupting its owning thread's turn — the exact mechanism the
  // Activities "cancel" action uses (resolve the thread's environment, then interruptTurn).
  const threadRefs = useThreadRefs();
  const interruptTurn = useAtomCommand(threadEnvironment.interruptTurn, { reportFailure: false });
  /**
   * Stop a run — on the SERVER, which is the only place that can actually end it.
   *
   * This used to send a client-side `interruptTurn` and nothing else, and only when the run's
   * thread happened to be in `threadRefs`. A workflow's own `⚡` thread usually is not, so the
   * button did nothing at all; and even when it fired, no one flipped the run row, so a run
   * orphaned by a container restart stayed "running" with no way to clear it. The route
   * interrupts the turn if one is live and marks the run stopped either way.
   *
   * The local interrupt stays as a fast path: it ends the turn in this tab's live session
   * immediately rather than waiting on the round trip.
   */
  const stopRun = useCallback(
    async (threadId: string | null, runId?: string) => {
      const ref = threadId ? threadRefs.find((r) => r.threadId === threadId) : undefined;
      if (ref && threadId) {
        await interruptTurn({
          environmentId: ref.environmentId,
          input: { threadId: threadId as ThreadId },
        }).catch(() => undefined);
      }
      if (runId) {
        await postJson("/api/morrow/workflows/runs/stop", { runId });
      }
      // Pull server truth back so the stopped run's status updates.
      await refreshNow();
    },
    [threadRefs, interruptTurn, refreshNow],
  );

  useEffect(() => {
    // Wait for Clerk: a fetch fired during session bootstrap 401s, and this page's first load
    // used to be exactly that race. Re-runs when the auth state settles.
    if (!isLoaded) return;
    void refresh();
  }, [refresh, isLoaded, isSignedIn]);

  // Which apps are already connected — for the dot marker in the picker.
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    void getJson<{ connected?: string[] }>(
      `/api/morrow/composio/connections?userId=${encodeURIComponent(userId)}`,
    ).then((res) => {
      if (alive) setConnected(new Set(res && Array.isArray(res.connected) ? res.connected : []));
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  // Load the vault list once granular vault access is turned on (for the item picker).
  useEffect(() => {
    if (!caps.vault || vaultAll.length > 0) return;
    let alive = true;
    void fetchVaultItems().then((items) => {
      if (alive) setVaultItemsList(items);
    });
    return () => {
      alive = false;
    };
  }, [caps.vault, vaultAll.length]);

  const toggleVaultItem = useCallback((id: string) => {
    setVaultSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const resetForm = useCallback(() => {
    setName("");
    setInstruction("");
    setAllowedApps([]);
    setCaps({ browser: false, memoryRead: true, memoryWrite: false, vault: false });
    setAutonomy("approval");
    setPending([]);
    setVaultSel([]);
  }, []);

  const createWorkflow = useCallback(async () => {
    setBusy(true);
    // Granular vault: fold the selected items into the capabilities grant. Empty
    // selection with vault on = all files; specific selection = only those.
    const vaultItems =
      caps.vault && vaultSel.length > 0
        ? vaultSel.map((id) => ({
            id,
            name: vaultAll.find((v) => v.id === id)?.name ?? id,
          }))
        : [];
    // SAY SO WHEN IT FAILS. This used to go through `postJson`, which turns every failure —
    // a plan gate, a 500, a dropped connection — into null, and then closed the modal and wiped
    // the form regardless. A failed create was indistinguishable from a successful one: the
    // dialog shut and no workflow appeared, with nothing to report. Keep the dialog and the
    // typing, and show what went wrong.
    const res = await postJsonResult<{ id?: string }>("/api/morrow/workflows", {
      name: name.trim() || "Untitled workflow",
      instruction: instruction.trim(),
      allowedApps,
      capabilities: { ...caps, vaultItems },
      autonomy,
    });
    setBusy(false);
    // null = a 402 upgrade modal is already up; a second message would stack on top of it.
    if (!res) return;
    if (!res.ok || !res.data?.id) {
      setActionError(res.data?.error?.trim() || t("workflows.create.failed"));
      return;
    }
    const created = res.data;
    setActionError(null);
    // Create every composed trigger (any mix of kinds). Sequential keeps ordering and
    // avoids hammering the API; a workflow with no triggers is simply run from chat.
    for (const p of pending) {
      await createTriggerFor(created.id as string, p);
    }
    setShowCreate(false);
    resetForm();
    void refreshNow();
    // Move to the setup page — the "next step" where the AI drafts what it does and
    // you can test-run it — instead of leaving an inert row.
    void navigate({ to: "/workflows/$id", params: { id: created.id as string } });
  }, [
    name,
    instruction,
    allowedApps,
    caps,
    autonomy,
    pending,
    vaultSel,
    vaultAll,
    refresh,
    resetForm,
    navigate,
    t,
  ]);

  /**
   * Rename from the list. Optimistic so the new name appears as you press Enter, and reverted by
   * the component when the server refuses — the dedicated /rename route touches only the name, so
   * this cannot clobber the instruction or the graph the way /update would.
   */
  const renameWorkflow = useCallback(
    async (id: string, name: string): Promise<boolean> => {
      const previous = workflows;
      setWorkflows((current) => current.map((w) => (w.id === id ? { ...w, name } : w)));
      const res = await postJsonResult<{ name?: string }>("/api/morrow/workflows/rename", {
        id,
        name,
      });
      // null = the 402 modal is already up.
      if (!res) return false;
      if (!res.ok) {
        setWorkflows(previous);
        setActionError(res.data?.error?.trim() || t("workflows.rename.failed"));
        return false;
      }
      setActionError(null);
      return true;
    },
    [workflows, t],
  );

  /**
   * Pause / resume from the list. Optimistic, and reverted if the server refuses. The dedicated
   * /set-enabled route flips only the `enabled` flag, so it can't clobber the definition.
   */
  const toggleEnabled = useCallback(
    async (id: string, enabled: boolean): Promise<void> => {
      const previous = workflows;
      setWorkflows((current) => current.map((w) => (w.id === id ? { ...w, enabled } : w)));
      const res = await postJsonResult<{ enabled?: boolean }>("/api/morrow/workflows/set-enabled", {
        id,
        enabled,
      });
      if (!res) return; // 402 modal already up
      if (!res.ok) {
        setWorkflows(previous);
        setActionError(t("workflows.toggle.failed"));
        return;
      }
      setActionError(null);
      invalidateCache("/api/morrow/workflow");
    },
    [workflows, t],
  );

  const deleteWorkflow = useCallback(
    async (id: string) => {
      await postJson("/api/morrow/workflows/delete", { id });
      void refreshNow();
    },
    [refresh],
  );

  /** Server-side copy — a routine's schedule comes along with it, so this is also what the
   *  Routines page calls. The copy only shows up once the list is pulled back. */
  const duplicateWorkflow = useCallback(
    async (id: string) => {
      const res = await postJsonResult<{ id?: string }>("/api/morrow/workflows/duplicate", { id });
      // null = the 402 upgrade modal is already up; a second message would just stack on it.
      if (!res) return;
      if (!res.ok) {
        setActionError(t("workflows.duplicate.failed"));
        return;
      }
      setActionError(null);
      await refreshNow();
    },
    [refresh, t],
  );

  /** The server returns the whole export file; the browser only has to save it. */
  const exportWorkflow = useCallback(
    async (wf: Workflow) => {
      const res = await getJson<{ export?: unknown }>(
        `/api/morrow/workflows/export?id=${encodeURIComponent(wf.id)}`,
      );
      if (!res?.export) {
        setActionError(t("workflows.export.failed"));
        return;
      }
      setActionError(null);
      downloadWorkflowJson(wf.name, res.export);
    },
    [t],
  );

  /**
   * Read a picked file, refuse it here if it clearly is not ours, then let the server have
   * the last word. Whatever it reports as missing is shown rather than dropped: an import
   * that silently loses an app-event trigger looks like a working copy until it doesn't run.
   */
  const importFile = useCallback(
    async (file: File) => {
      setImporting(true);
      setActionError(null);
      let text = "";
      try {
        text = await file.text();
      } catch {
        setImporting(false);
        setImportNotice({ kind: "error", reason: "unreadable" });
        return;
      }
      const parsed = parseWorkflowExportFile(text);
      if (!parsed.ok) {
        setImporting(false);
        setImportNotice({ kind: "error", reason: parsed.reason });
        return;
      }
      const res = await postJsonResult<{ id?: string; warnings?: string[] }>(
        "/api/morrow/workflows/import",
        { export: parsed.file as unknown as Record<string, unknown> },
      );
      setImporting(false);
      if (!res) return;
      if (!res.ok) {
        setImportNotice({ kind: "error", reason: "rejected", detail: res.data?.error });
        return;
      }
      setImportNotice({
        kind: "ok",
        id: res.data?.id ?? null,
        warnings: res.data?.warnings ?? [],
        // The exporter already recorded what it could not take; the importer's warnings
        // say what to do about it, so the user wants both.
        dropped: (parsed.file as WorkflowExportFile).dropped,
      });
      await refreshNow();
    },
    [refresh],
  );

  const onImportFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0];
      // Clear it so picking the same file twice still fires a change.
      event.currentTarget.value = "";
      if (file) void importFile(file);
    },
    [importFile],
  );

  return (
    <>
      <MorrowWorkspacePage
        title={t("workflows.title")}
        subtitle={t("workflows.subtitle")}
        icon={<WorkflowIcon />}
        wide
        contentClassName="max-w-4xl mx-auto w-full"
        actionClassName="items-center gap-1"
        action={
          <>
            {/* The Import button is the control; this is only the file dialog it opens. sr-only
                kept it in the a11y tree, where it announced a second, unlabelled "Choose file"
                next to Import (and tripped the "form field needs an id or name" warning). */}
            <input
              ref={importInputRef}
              name="workflow-import-file"
              type="file"
              accept=".json,application/json"
              className="sr-only"
              tabIndex={-1}
              aria-hidden="true"
              onChange={onImportFileChange}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={importing}
              onClick={() => importInputRef.current?.click()}
            >
              {importing ? (
                <Loader2Icon className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <UploadIcon aria-hidden="true" />
              )}
              {importing ? t("workflows.import.reading") : t("workflows.import")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-sky-500 [--control-icon-color:var(--color-sky-500)] hover:bg-sky-500/10 hover:text-sky-500"
              onClick={() => setShowCreate(true)}
            >
              <PlusIcon aria-hidden="true" />
              {t("workflows.create")}
            </Button>
          </>
        }
      >
        {actionError ? (
          <p className="mb-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-[13px] text-red-500">
            {actionError}
          </p>
        ) : null}
        {!loaded ? (
          <ul className="flex flex-col gap-3" aria-hidden>
            {[0, 1, 2].map((i) => (
              <li
                key={i}
                className="card-glass flex flex-col gap-3 rounded-xl border border-transparent p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                    <div className="mt-2 h-3 w-full max-w-md animate-pulse rounded bg-muted/70" />
                    <div className="mt-3 h-2.5 w-28 animate-pulse rounded bg-muted/50" />
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <div className="h-5 w-9 animate-pulse rounded-full bg-muted" />
                    {[0, 1, 2, 3].map((k) => (
                      <div key={k} className="size-8 animate-pulse rounded-md bg-muted" />
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : loadFailed && workflows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <p className="text-sm text-muted-foreground">{t("workflows.loadFailed")}</p>
            <Button type="button" size="sm" variant="outline" onClick={() => void refreshNow()}>
              {t("workflows.loadRetry")}
            </Button>
          </div>
        ) : workflows.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground/70">
            {t("workflows.empty", { create: t("workflows.create.emptyCreate") })}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {workflows.map((wf) => (
              <WorkflowCard
                key={wf.id}
                workflow={wf}
                triggerCount={
                  triggers.filter((t) => t.workflowId === wf.id).length +
                  wfTriggers.filter((t) => t.workflowId === wf.id).length
                }
                runs={runsByWf[wf.id] ?? []}
                onStopRun={stopRun}
                onDuplicate={() => void duplicateWorkflow(wf.id)}
                onExport={() => void exportWorkflow(wf)}
                onRename={(name) => renameWorkflow(wf.id, name)}
                onToggleEnabled={(enabled) => void toggleEnabled(wf.id, enabled)}
                onDelete={() => void deleteWorkflow(wf.id)}
              />
            ))}
          </ul>
        )}
      </MorrowWorkspacePage>

      {/* Create modal */}
      {showCreate ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="my-8 flex w-full max-w-2xl flex-col gap-3 rounded-2xl border border-border bg-popover p-5 text-popover-foreground shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">
                {t("workflows.new.title")}
              </h2>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <XIcon className="size-4" />
              </button>
            </div>
            <input
              className={INPUT}
              placeholder={t("workflows.new.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <textarea
              className={`${INPUT} min-h-[110px] resize-y`}
              placeholder={t("workflows.new.instructionPlaceholder")}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
            />

            <button
              type="button"
              onClick={() => setManual((v) => !v)}
              className="w-fit text-[12px] font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              {manual ? t("workflows.new.manualHide") : t("workflows.new.manualShow")}
            </button>

            {manual ? (
              <div className="grid gap-3 md:grid-cols-2 md:items-start">
                {/* How it runs — compose ANY mix of triggers (or none = runs from chat). */}
                <div className="flex flex-col gap-2 rounded-lg border border-border/60 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("workflows.new.triggersSection")}
                  </div>
                  {pending.length > 0 ? (
                    <ul className="flex flex-col gap-1.5">
                      {pending.map((p) => (
                        <li
                          key={p.uid}
                          className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/30 px-2.5 py-1.5 text-[13px]"
                        >
                          <span className="truncate text-muted-foreground">
                            {pendingLabel(p, t, channelLabel)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removePending(p.uid)}
                            className="shrink-0 text-muted-foreground hover:text-foreground"
                            aria-label="Remove trigger"
                          >
                            <XIcon className="size-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[11px] text-muted-foreground/70">
                      {t("workflows.new.noTriggers")}
                    </p>
                  )}
                  <TriggerComposer
                    userId={userId}
                    onAdd={addPending}
                    hasWhatsapp={pending.some((p) => p.kind === "whatsapp")}
                    openOn={triggerSourceOf(pending.map((p) => p.kind))}
                  />
                </div>

                {/* Access — least-privilege grant. The run is fenced to exactly this. */}
                <div className="flex flex-col gap-3 rounded-lg border border-border/60 p-3">
                  <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("workflows.new.appsSection")}
                    </div>
                    <AppScopePicker
                      selected={allowedApps}
                      metaBySlug={metaBySlug}
                      connected={connected}
                      onToggle={toggleApp}
                    />
                  </div>

                  <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("workflows.new.capabilities")}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-foreground">
                      {(
                        [
                          ["browser", t("workflows.new.cap.browser")],
                          ["memoryRead", t("workflows.new.cap.memoryRead")],
                          ["memoryWrite", t("workflows.new.cap.memoryWrite")],
                          ["vault", t("workflows.new.cap.vault")],
                        ] as ReadonlyArray<[keyof Caps, string]>
                      ).map(([key, label]) => (
                        <label key={key} className="flex cursor-pointer items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={caps[key]}
                            onChange={() => toggleCap(key)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>

                    {/* Granular vault: when Files/vault is on, scope to specific items (none = all). */}
                    {caps.vault ? (
                      <div className="mt-2 rounded-md border border-border/60 p-2.5">
                        <div className="mb-1 text-[11px] text-muted-foreground">
                          {vaultSel.length === 0
                            ? t("workflows.new.vaultAll")
                            : t("workflows.new.vaultCount", { count: vaultSel.length })}
                        </div>
                        {vaultAll.length === 0 ? (
                          <p className="text-[11px] text-muted-foreground/60">
                            {t("workflows.new.vaultEmpty")}
                          </p>
                        ) : (
                          <div className="flex max-h-32 flex-col gap-1 overflow-y-auto">
                            {vaultAll.map((v) => (
                              <label
                                key={v.id}
                                className="flex cursor-pointer items-center gap-2 text-[12px] text-foreground/90"
                              >
                                <input
                                  type="checkbox"
                                  checked={vaultSel.includes(v.id)}
                                  onChange={() => toggleVaultItem(v.id)}
                                />
                                <span className="truncate">{v.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("workflows.new.autonomy")}
                    </span>
                    <select
                      className={`${INPUT} w-auto`}
                      value={autonomy}
                      onChange={(e) => setAutonomy(e.target.value as Autonomy)}
                    >
                      <option value="read">{t("workflows.new.autonomy.read")}</option>
                      <option value="approval">{t("workflows.new.autonomy.approval")}</option>
                      <option value="autonomous">{t("workflows.new.autonomy.autonomous")}</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="flex justify-end">
              <button
                type="button"
                className={PRIMARY_BUTTON}
                onClick={() => void createWorkflow()}
                disabled={busy}
              >
                {busy ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  t("workflows.new.createButton")
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Import result. A modal rather than a toast on purpose: the warnings are the only
          place the user learns that e.g. a vault folder or an app-event trigger did not come
          across, and a copy that quietly lost its trigger reads as a working copy. */}
      {importNotice ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm"
          onClick={() => setImportNotice(null)}
        >
          <div
            className="my-16 flex w-full max-w-md flex-col gap-3 rounded-2xl border border-border bg-popover p-5 text-popover-foreground shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-semibold text-foreground">
              {importNotice.kind === "ok"
                ? t("workflows.import.okTitle")
                : t("workflows.import.errorTitle")}
            </h2>

            {importNotice.kind === "error" ? (
              <p className="text-[13px] leading-[1.6] text-muted-foreground">
                {t(IMPORT_ERROR_KEY[importNotice.reason])}
                {importNotice.detail ? ` ${importNotice.detail}` : ""}
              </p>
            ) : (
              <>
                <p className="text-[13px] leading-[1.6] text-muted-foreground">
                  {t("workflows.import.okBody")}
                </p>
                {importNotice.warnings.length > 0 ? (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("workflows.import.warningsTitle")}
                    </div>
                    <ul className="mt-1.5 list-disc pl-5 text-[13px] leading-[1.6] text-amber-600 dark:text-amber-400">
                      {importNotice.warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {removedEntries(importNotice.dropped).length > 0 ? (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("workflows.import.droppedTitle")}
                    </div>
                    <ul className="mt-1.5 list-disc pl-5 text-[13px] leading-[1.6] text-muted-foreground">
                      {removedEntries(importNotice.dropped).map((entry) => (
                        <li key={entry.key}>
                          {t(REMOVED_LABEL_KEY[entry.key], { count: entry.count })}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {importNotice.warnings.length === 0 &&
                removedEntries(importNotice.dropped).length === 0 ? (
                  <p className="text-[13px] text-muted-foreground/80">
                    {t("workflows.import.cleanBody")}
                  </p>
                ) : null}
              </>
            )}

            <div className="flex items-center justify-end gap-2">
              {importNotice.kind === "ok" && importNotice.id ? (
                <button
                  type="button"
                  className={GHOST_BUTTON}
                  onClick={() => {
                    const id = importNotice.id;
                    setImportNotice(null);
                    if (id) {
                      void navigate({
                        to: "/workflows/$id",
                        params: { id },
                        search: { tab: "edit" },
                      });
                    }
                  }}
                >
                  {t("workflows.import.openWorkflow")}
                </button>
              ) : null}
              <button
                type="button"
                className={PRIMARY_BUTTON}
                onClick={() => setImportNotice(null)}
              >
                {t("workflows.import.dismiss")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function WorkflowCard(props: {
  workflow: Workflow;
  triggerCount: number;
  runs: readonly WorkflowRunLite[];
  /** The run id is what makes stopping terminal — the thread alone cannot clear the row. */
  onStopRun: (threadId: string | null, runId?: string) => void | Promise<void>;
  onDuplicate: () => void;
  onExport: () => void;
  /** Commit a new name; false puts the old one back (the page shows the error). */
  onRename: (name: string) => Promise<boolean>;
  /** Pause (false) / resume (true) — the kill switch the server stores as `enabled`. */
  onToggleEnabled: (enabled: boolean) => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const { workflow, triggerCount, runs } = props;
  const enabled = workflow.enabled;
  const runCount = runs.length;
  // Compact on purpose: five of these plus a switch sit in the card's top-right corner, and at
  // 32px with 16px glyphs the cluster outweighed the card's own title. 28px with 14px glyphs
  // reads as a toolbar rather than a row of buttons.
  const ICON_BTN =
    "inline-flex size-7 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition hover:bg-muted/60 hover:text-foreground";
  const [showRuns, setShowRuns] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [stopping, setStopping] = useState<string | null>(null);
  const recentRuns = runs.slice(0, 5);

  const handleStop = useCallback(
    async (run: WorkflowRunLite) => {
      setStopping(run.id);
      await props.onStopRun(run.threadId, run.id);
      setStopping(null);
    },
    [props],
  );

  return (
    <li
      className={`card-glass flex flex-col gap-3 rounded-xl border border-transparent p-4 transition hover:border-border ${
        enabled ? "" : "opacity-60"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="block min-w-0 flex-1">
          {/* The name is its own control: clicking it renames in place. It therefore sits OUTSIDE
              the link — inside, every click would navigate to the workflow instead. */}
          <h3 className="flex items-center gap-2 truncate text-sm font-semibold text-foreground">
            <InlineRename
              value={workflow.name}
              onRename={(next) => props.onRename(next)}
              inputClassName="text-sm font-semibold"
            />
            {enabled ? null : (
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("workflows.card.paused")}
              </span>
            )}
          </h3>
          <Link
            to="/workflows/$id"
            params={{ id: workflow.id }}
            className="block min-w-0"
            aria-label={workflow.name}
          >
            <p className="mt-1 line-clamp-2 text-[13px] leading-[1.5] text-muted-foreground/80">
              {workflow.instruction?.trim() || t("workflows.card.noDescription")}
            </p>
            <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span>
                {triggerCount === 1
                  ? t("workflows.card.triggers", { count: triggerCount })
                  : t("workflows.card.triggersPlural", { count: triggerCount })}
              </span>
              <span>·</span>
              <span>
                {runCount === 1
                  ? t("workflows.card.runs", { count: runCount })
                  : t("workflows.card.runsPlural", { count: runCount })}
              </span>
            </div>
          </Link>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {/* Enable / pause — a real switch so the on/off state reads at a glance. */}
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => props.onToggleEnabled(!enabled)}
            title={enabled ? t("workflows.card.pause") : t("workflows.card.enable")}
            aria-label={enabled ? t("workflows.card.pause") : t("workflows.card.enable")}
            className={`relative mr-0.5 h-4 w-7 shrink-0 rounded-full transition ${
              enabled ? "bg-primary" : "bg-muted"
            }`}
          >
            <span
              className={`absolute top-0.5 size-3 rounded-full bg-background shadow transition-all ${
                enabled ? "left-[14px]" : "left-0.5"
              }`}
            />
          </button>
          {runCount > 0 ? (
            <button
              type="button"
              className={ICON_BTN}
              onClick={() => setShowRuns((v) => !v)}
              title={showRuns ? t("workflows.card.hideRuns") : t("workflows.card.viewRuns")}
              aria-label={showRuns ? t("workflows.card.hideRuns") : t("workflows.card.viewRuns")}
            >
              <ListIcon className="size-3.5" />
            </button>
          ) : null}
          <Link
            to="/workflows/$id"
            params={{ id: workflow.id }}
            search={{ tab: "edit" }}
            className={ICON_BTN}
            title={t("workflows.card.edit")}
            aria-label={t("workflows.card.edit")}
          >
            <PencilIcon className="size-3.5" />
          </Link>
          <button
            type="button"
            className={ICON_BTN}
            onClick={props.onDuplicate}
            title={t("workflows.card.duplicate")}
            aria-label={t("workflows.card.duplicate")}
          >
            <CopyIcon className="size-3.5" />
          </button>
          <button
            type="button"
            className={ICON_BTN}
            onClick={props.onExport}
            title={t("workflows.card.export")}
            aria-label={t("workflows.card.export")}
          >
            <DownloadIcon className="size-3.5" />
          </button>
          <button
            type="button"
            className={`${ICON_BTN} hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-500`}
            onClick={props.onDelete}
            title={t("workflows.card.delete")}
            aria-label={t("workflows.card.delete")}
          >
            <Trash2Icon className="size-3.5" />
          </button>
        </div>
      </div>

      {showRuns && recentRuns.length > 0 ? (
        <ul className="flex flex-col gap-1.5 border-t border-border/50 pt-3">
          {recentRuns.map((r) => (
            <li key={r.id} className="flex min-w-0 flex-col gap-1">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`size-2 shrink-0 rounded-full ${
                    r.status === "running"
                      ? "bg-primary"
                      : r.status === "ok"
                        ? "bg-emerald-500"
                        : r.status === "skipped"
                          ? "bg-muted-foreground/50"
                          : "bg-red-500"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setSelectedRunId((cur) => (cur === r.id ? null : r.id))}
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-[12px]"
                >
                  <span className="shrink-0 font-medium capitalize text-foreground">
                    {r.source}
                  </span>
                  {runDetailLabel(r.detail, t) ? (
                    <span className="truncate text-muted-foreground">
                      · {runDetailLabel(r.detail, t)}
                    </span>
                  ) : null}
                  <span className="ml-auto shrink-0 text-[10px] capitalize text-muted-foreground/60">
                    {r.status === "running"
                      ? "Running…"
                      : r.status === "ok"
                        ? "Completed"
                        : r.status === "skipped"
                          ? "Skipped"
                          : "Failed"}
                  </span>
                </button>
                {r.status === "running" ? (
                  <button
                    type="button"
                    className={`${GHOST_BUTTON} shrink-0`}
                    disabled={stopping === r.id}
                    onClick={() => void handleStop(r)}
                  >
                    {stopping === r.id ? (
                      <Loader2Icon className="size-3.5 animate-spin" />
                    ) : (
                      t("workflows.card.stopRun")
                    )}
                  </button>
                ) : null}
              </div>
              {selectedRunId === r.id ? (
                <RunStepsPanel
                  runId={r.id}
                  graph={null}
                  runStatus={r.status}
                  threadId={r.threadId}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * The source a trigger picker should open on, given the kinds a workflow already has.
 *
 * Opening on something the workflow does NOT use is actively misleading — it reads as existing
 * configuration. A workflow with no triggers is not unconfigured: it runs from chat, which is why
 * no kinds at all resolves to "chat" rather than leaving the caller to guess a channel.
 */
export function triggerSourceOf(kinds: ReadonlyArray<string>): TriggerSource | undefined {
  if (kinds.length === 0) return "chat";
  for (const kind of kinds) {
    if (kind === "whatsapp") return "whatsapp";
    if (kind === "comms") return "comms";
    if (kind === "schedule") return "schedule";
    if (kind === "vault-item-added") return "vault";
    if (kind === "trigger" || kind === "triggers") return "triggers";
  }
  return undefined;
}
